// File-name rules shared by uploads and downloads.
//
// A name with extra dots ("185397_V-2-2.1.csv") breaks downloads: the browser
// takes everything after the last dot as the file type, so the export saved
// as ".1" instead of ".csv". Uploads are checked up front (with a message that
// says how to rename), and every download name is cleaned the same way.

// Characters Windows / macOS don't allow in file names.
const INVALID_CHARS = /[\\/:*?"<>|\u0000-\u001F]/;
const INVALID_CHARS_GLOBAL = /[\\/:*?"<>|\u0000-\u001F]+/g;
const MAX_NAME_LENGTH = 150;

const splitExtension = (name: string) => {
    // ".csv" is an extension with no name (base ""), same as the backend's rpartition.
    const dot = name.lastIndexOf(".");
    return dot >= 0 ? { base: name.slice(0, dot), ext: name.slice(dot + 1) } : { base: name, ext: "" };
};

/** Why this upload's name isn't acceptable, or null. Must match
 *  validate_upload_filename in backend/app/utils/bulk_file_parser.py. */
export const uploadFileNameError = (fileName: string): string | null => {
    const name = (fileName || "").trim();
    const { base, ext } = splitExtension(name);
    const suggestion = `${base.replace(/\./g, "-").replace(INVALID_CHARS_GLOBAL, "-")}${ext ? `.${ext}` : ""}`;
    if (!base.trim()) {
        return "The file needs a name. Please rename it and upload again.";
    }
    if (base.includes(".")) {
        return `File names can only have one dot, before the extension. Please rename it to "${suggestion}" and upload again.`;
    }
    if (INVALID_CHARS.test(base)) {
        return `File names can't contain \\ / : * ? " < > |. Please rename it to "${suggestion}" and upload again.`;
    }
    if (name.length > MAX_NAME_LENGTH) {
        return `The file name is too long (over ${MAX_NAME_LENGTH} characters). Please shorten it and upload again.`;
    }
    return null;
};

/** A safe download name: the original name without its extension, extra dots
 *  and invalid characters turned into "-", always ending in `.${ext}`. */
export const downloadFileName = (original: string | null | undefined, fallback: string, ext = "csv"): string => {
    const raw = (original || "").trim();
    const { base } = splitExtension(raw);
    const clean = base
        .replace(/\./g, "-")
        .replace(INVALID_CHARS_GLOBAL, "-")
        .replace(/-{2,}/g, "-")
        .replace(/^[-\s]+|[-\s]+$/g, "");
    return `${clean || fallback}.${ext}`;
};
